import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermission } from '@/Hooks/usePermission';
import { api } from '@/lib/api';
import { useBranches } from '@/Pages/Operations/shared';
import { Card, CardContent } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import { addDaysIso, apiError, fmtHours, isoToday, type LabourReport } from './schedulingLib';

/**
 * Scheduled versus actual, in HOURS — never cost. `scheduling.view` does not
 * imply `compensation.view` (ADR-0008 §4), and a rate is what a labour COST
 * report would need; this stays on the headcount-and-hours side of that
 * line, which is also why the backend requires `scheduling.view` AND
 * `reports.view` together (`@RequirePermissions` is an AND, not an OR — see
 * `PermissionsGuard`) rather than either alone.
 */
export default function LabourTab() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const branches = useBranches();
  const allowed = can('scheduling.view', 'reports.view');

  const [branchId, setBranchId] = useState('');
  const [from, setFrom] = useState(addDaysIso(isoToday(), -7));
  const [to, setTo] = useState(isoToday());
  const [report, setReport] = useState<LabourReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!branchId && branches.length > 0) setBranchId(branches[0].id);
  }, [branchId, branches]);

  const load = useCallback(() => {
    if (!allowed || !branchId) return;
    setLoading(true);
    setError(null);
    api
      .get<LabourReport>('/scheduling/reports/labour', { params: { branchId, from, to } })
      .then(({ data }) => setReport(data))
      .catch((e) => setError(apiError(e, t('scheduling.labour.loadFailed'))))
      .finally(() => setLoading(false));
  }, [allowed, branchId, from, to, t]);

  useEffect(load, [load]);

  if (!allowed) {
    return (
      <EmptyState title={t('scheduling.labour.noAccess')} hint={t('scheduling.labour.noAccessHint')} />
    );
  }

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          {branches.length > 0 && (
            <Field label={t('scheduling.roster.branch')}>
              <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Field label={t('scheduling.labour.from')}>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} max={to} />
          </Field>
          <Field label={t('scheduling.labour.to')}>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} min={from} max={isoToday()} />
          </Field>
        </CardContent>
      </Card>

      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : !report || report.days.every((d) => d.scheduledMinutes === 0 && d.actualMinutes === 0) ? (
        <EmptyState title={t('scheduling.labour.empty')} hint={t('scheduling.labour.emptyHint')} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {(
              [
                ['scheduling.labour.scheduled', report.totals.scheduledMinutes],
                ['scheduling.labour.actual', report.totals.actualMinutes],
                ['scheduling.labour.overtime', report.totals.overtimeMinutes],
                ['scheduling.labour.variance', report.totals.varianceMinutes],
              ] as const
            ).map(([key, minutes]) => (
              <Card key={key}>
                <CardContent className="p-4">
                  <p className="text-xs text-ink-muted">{t(key)}</p>
                  <p className="text-lg font-semibold tabular-nums">
                    {minutes > 0 && key === 'scheduling.labour.variance' ? '+' : ''}
                    {fmtHours(minutes)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>{t('scheduling.labour.date')}</TH>
                  <TH className="text-end">{t('scheduling.labour.scheduled')}</TH>
                  <TH className="text-end">{t('scheduling.labour.actual')}</TH>
                  <TH className="text-end">{t('scheduling.labour.overtime')}</TH>
                  <TH className="text-end">{t('scheduling.labour.variance')}</TH>
                  <TH className="text-end">{t('scheduling.labour.headcount')}</TH>
                </TR>
              </THead>
              <tbody>
                {report.days.map((day) => (
                  <TR key={day.date}>
                    <TD className="tabular-nums">{day.date}</TD>
                    <TD className="text-end tabular-nums">{fmtHours(day.scheduledMinutes)}</TD>
                    <TD className="text-end tabular-nums">{fmtHours(day.actualMinutes)}</TD>
                    <TD className="text-end tabular-nums">{fmtHours(day.overtimeMinutes)}</TD>
                    <TD
                      className={`text-end tabular-nums ${day.varianceMinutes > 0 ? 'text-amber-600' : ''}`}
                    >
                      {day.varianceMinutes > 0 ? '+' : ''}
                      {fmtHours(day.varianceMinutes)}
                    </TD>
                    <TD className="text-end tabular-nums">
                      {day.scheduledHeadcount} / {day.actualHeadcount}
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </>
      )}
    </div>
  );
}
